import { EMAIL_REGEX } from '@/utils/emailRegex';
import { isPediatricsEligible } from '@/utils/pediatrics';
import type { StepConfig, ValidationResult, WizardState } from '../types';

/**
 * Checks if any member is eligible for pediatrics (age 21 or under).
 */
function hasEligiblePediatricsMember(state: WizardState): boolean {
  const members = state.formData?.members || [];
  return members.some(member => {
    if (!member.date_of_birth) return false;
    return isPediatricsEligible(member.date_of_birth);
  });
}

/**
 * Validates that signup_type is selected
 */
function validateSignupType(state: WizardState): ValidationResult {
  if (!state.formData?.signup_type) {
    return {
      isValid: false,
      errors: { signup_type: 'Please select an option' },
    };
  }
  return { isValid: true, errors: {} };
}

/**
 * Validates ZIP code format
 */
function validateZipCode(state: WizardState): ValidationResult {
  const zipCode = state.formData?.zip_code?.trim();
  if (!zipCode) {
    return {
      isValid: false,
      errors: { zip_code: 'ZIP code is required' },
    };
  }
  if (!/^\d{5}(-\d{4})?$/.test(zipCode)) {
    return {
      isValid: false,
      errors: { zip_code: 'Please enter a valid US ZIP code' },
    };
  }
  return { isValid: true, errors: {} };
}

/**
 * LocationCheck is informational, always valid
 */
function validateLocationCheck(_state: WizardState): ValidationResult {
  return { isValid: true, errors: {} };
}

/**
 * Validates that at least one reason is selected
 */
function validateReasons(state: WizardState): ValidationResult {
  const reasons = state.formData?.reasons || [];
  if (reasons.length === 0) {
    return {
      isValid: false,
      errors: { reasons: 'Please select at least one option' },
    };
  }
  return { isValid: true, errors: {} };
}

/**
 * Validates that urgency is selected
 */
function validateUrgency(state: WizardState): ValidationResult {
  if (!state.formData?.urgency) {
    return {
      isValid: false,
      errors: { urgency: 'Please select an option' },
    };
  }
  return { isValid: true, errors: {} };
}

/**
 * Validates that pcp_status is selected
 */
function validatePcpStatus(state: WizardState): ValidationResult {
  if (!state.formData?.pcp_status) {
    return {
      isValid: false,
      errors: { pcp_status: 'Please select an option' },
    };
  }
  return { isValid: true, errors: {} };
}

/**
 * Validates contact details including phone
 */
function validateContactInfo(state: WizardState): ValidationResult {
  const errors: Record<string, string> = {};
  const contact = state.formData?.contact_details;

  if (!contact) {
    return {
      isValid: false,
      errors: { contact_details: 'Contact details are required' },
    };
  }

  if (!contact.first_name?.trim()) {
    errors.first_name = 'First name is required';
  }

  if (!contact.last_name?.trim()) {
    errors.last_name = 'Last name is required';
  }

  if (!contact.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_REGEX.test(contact.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!contact.phone?.trim()) {
    errors.phone = 'Phone number is required';
  } else {
    const cleaned = contact.phone.replace(/\D/g, '');
    if (!(
      cleaned.length === 10 ||
      (cleaned.length === 11 && cleaned[0] === '1')
    )) {
      errors.phone = 'Please enter a valid phone number';
    }
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

/**
 * Validates member details
 */
function validateMemberDetails(state: WizardState): ValidationResult {
  const errors: Record<string, string> = {};
  const members = state.formData?.members;

  if (!members || members.length === 0) {
    errors.members = 'At least one member is required';
    return { isValid: false, errors };
  }

  members.forEach((member, index) => {
    if (!member.first_name?.trim()) {
      errors[`member_${index}_first_name`] = 'First name is required';
    }
    if (!member.last_name?.trim()) {
      errors[`member_${index}_last_name`] = 'Last name is required';
    }
    if (!member.date_of_birth) {
      errors[`member_${index}_date_of_birth`] = 'Date of birth is required';
    } else {
      const dob = new Date(member.date_of_birth);
      const today = new Date();
      const minDate = new Date(today.getFullYear() - 120, 0, 1);
      if (isNaN(dob.getTime()) || dob > today || dob < minDate) {
        errors[`member_${index}_date_of_birth`] =
          'Please enter a valid date of birth';
      }
    }
  });

  return { isValid: Object.keys(errors).length === 0, errors };
}

/**
 * PediatricsUpsell has no validation (action buttons handle state)
 */
function validatePediatricsUpsell(_state: WizardState): ValidationResult {
  return { isValid: true, errors: {} };
}

/**
 * Quote is display only, always valid
 */
function validateQuote(_state: WizardState): ValidationResult {
  return { isValid: true, errors: {} };
}

/**
 * Step configuration for the intake v2 wizard.
 *
 * Each step defines:
 * - id: Unique identifier for the step
 * - slug: URL segment for the step (e.g., /intake-v2/signup-type)
 * - title: Display title for the step
 * - component: 'native' for React components, 'formsort' for Formsort embeds
 * - isVisible: Function to determine if step should be shown based on state
 * - validate: Function to validate step data before proceeding
 */
export const STEPS: StepConfig[] = [
  {
    id: 'signup-type',
    slug: 'signup-type',
    title: 'Who are you signing up?',
    component: 'native',
    isVisible: () => true,
    validate: validateSignupType,
  },
  {
    id: 'zip-code',
    slug: 'zip-code',
    title: 'Where do you live?',
    component: 'native',
    isVisible: () => true,
    validate: validateZipCode,
  },
  {
    id: 'location-check',
    slug: 'location-check',
    title: 'Your Nearest Location',
    component: 'native',
    isVisible: () => true,
    validate: validateLocationCheck,
  },
  {
    id: 'reasons',
    slug: 'reasons',
    title: 'What brings you to Sollis?',
    component: 'native',
    isVisible: () => true,
    validate: validateReasons,
  },
  {
    id: 'urgency',
    slug: 'urgency',
    title: 'When do you want to start this membership?',
    component: 'native',
    isVisible: () => true,
    validate: validateUrgency,
  },
  {
    id: 'pcp-status',
    slug: 'pcp-status',
    title: 'Primary Care Physician',
    component: 'native',
    isVisible: () => true,
    validate: validatePcpStatus,
  },
  {
    id: 'contact-info',
    slug: 'contact-info',
    title: 'Contact Information',
    component: 'native',
    isVisible: () => true,
    validate: validateContactInfo,
  },
  {
    id: 'member-details',
    slug: 'member-details',
    title: 'Member Details',
    component: 'native',
    isVisible: () => true,
    validate: validateMemberDetails,
  },
  {
    id: 'pediatrics-upsell',
    slug: 'pediatrics-upsell',
    title: 'Sollis Pediatrics',
    component: 'native',
    isVisible: state =>
      state.formData?.location_has_pediatrics === true &&
      hasEligiblePediatricsMember(state),
    validate: validatePediatricsUpsell,
  },
  {
    id: 'quote',
    slug: 'quote',
    title: 'Your Quote',
    component: 'native',
    isVisible: () => true,
    validate: validateQuote,
  },
  {
    id: 'quote-edit',
    slug: 'quote-edit',
    title: 'Your Quote',
    component: 'native',
    isVisible: () => false,
    validate: validateQuote,
    parent: 'quote',
  },
  {
    id: 'quote-members',
    slug: 'quote-members',
    title: 'Your Quote',
    component: 'native',
    isVisible: () => false,
    validate: validateQuote,
    parent: 'quote',
  },
];

/**
 * Get a step configuration by its slug
 */
export function getStepBySlug(slug: string): StepConfig | undefined {
  return STEPS.find(step => step.slug === slug);
}

/**
 * Get a step configuration by its ID
 */
export function getStepById(id: string): StepConfig | undefined {
  return STEPS.find(step => step.id === id);
}

/**
 * Get all visible steps based on current state
 */
export function getChildSteps(): StepConfig[] {
  return STEPS.filter(step => Boolean(step.parent));
}
/**
 * Get all visible steps based on current state
 */
export function getVisibleSteps(state: WizardState): StepConfig[] {
  return STEPS.filter(step => step.isVisible(state));
}

/**
 * Get the index of a step in the visible steps array
 */
export function getVisibleStepIndex(slug: string, state: WizardState): number {
  const visibleSteps = getVisibleSteps(state);
  const index = visibleSteps.findIndex(step => step.slug === slug);

  if (index > -1) return index;

  const childSteps = getChildSteps();
  const child = childSteps.find(step => step.slug === slug);

  return visibleSteps.findIndex(step => step.slug === child?.parent);
}

/**
 * Get the first incomplete step for a given state
 */
export function getFirstIncompleteStep(
  state: WizardState
): StepConfig | undefined {
  const visibleSteps = getVisibleSteps(state);
  return visibleSteps.find(step => !state.completedSteps.includes(step.id));
}

/**
 * Check if all visible steps are completed
 */
export function areAllStepsCompleted(state: WizardState): boolean {
  const visibleSteps = getVisibleSteps(state);
  return visibleSteps.every(step => state.completedSteps.includes(step.id));
}

const MEMBER_DETAILS_INDEX = STEPS.findIndex(s => s.id === 'member-details');

export const QUESTIONNAIRE_STEP_IDS = STEPS.slice(0, MEMBER_DETAILS_INDEX).map(
  s => s.id
);

export const POST_QUESTIONNAIRE_SLUGS = STEPS.slice(MEMBER_DETAILS_INDEX).map(
  s => s.slug
);
